// lomavisConfig.ts

export interface Location {
  uuid: string;
  slug: string;
  name: string;
}
export interface MediaLibrary {
  enabled: boolean;
  url: string;
}

export interface ConnectedPlatforms {
  facebook: boolean;
  facebook_group: boolean;
  instagram: boolean;
  google: boolean;
  twitter: boolean;
  linkedin_personal_account: boolean;
  linkedin_business_page: boolean;
  youtube: boolean;
  tiktok_personal_account: boolean;
  tiktok_business_page: boolean;
  whatsapp: boolean;
  pinterest: boolean;
  generic: boolean;
}

export interface BrowserExtensionData {
  domain: string;
  extracted_text: string;
  image_urls: {
    urls: string[];
    version: string;
  };
  url: string;
  uuid: string;
}

export interface PlatformDisplayData {
  display_name: string | null;
  profile_image_url: string | null;
}

export interface MentionSearchUrls {
  facebook: string;
  twitter: string;
}

export interface PlaceSearchUrls {
  facebook: string;
}

export interface Features {
  ai_enabled: boolean;
  recurring_posts: boolean;
  delayed_posts: boolean;
  publish_to_approval: boolean;
  multipost_templates_posts: boolean;
  bulk_upload: boolean;
  accessibility_guard: boolean;
  post_campaigns: boolean;
}

export interface lomavisCreatorDefaultData {
  instagram_location_id?: string;
  instagram_location_name?: string;
  facebook_location_id?: string;
  facebook_location_name?: string;
}

export interface BestTimeToPostOverview {
  best_hour: number | null;
  best_minute: number | null;
}

export interface BestTimeToPostConfig {
  enabled: boolean;
  overview?: BestTimeToPostOverview | null;
  platform_overview?: Record<string, BestTimeToPostOverview>;
}

export interface LomavisCreatorConfig {
  location: Location;
  media_library: MediaLibrary;
  lomavis_ai: any;
  cloud: any;
  is_multipost: boolean;
  connected_platforms: ConnectedPlatforms;
  supported_platforms: ConnectedPlatforms;
  load_media_files: any[];
  best_time_to_post: BestTimeToPostConfig | null;
  platform_display_data: {
    facebook: PlatformDisplayData;
    facebook_group: PlatformDisplayData;
    instagram: PlatformDisplayData;
    google: PlatformDisplayData;
    twitter: PlatformDisplayData;
    linkedin_personal_account: PlatformDisplayData;
    linkedin_business_page: PlatformDisplayData;
    youtube: PlatformDisplayData;
    tiktok_personal_account: PlatformDisplayData;
    tiktok_business_page: PlatformDisplayData;
    whatsapp: PlatformDisplayData;
    pinterest: PlatformDisplayData;
    generic: PlatformDisplayData;
  };
  mention_search_urls: MentionSearchUrls;
  place_search_urls: PlaceSearchUrls;
  timezone: string;
  features: Features;
  http_endpoint: string;
  is_update: boolean;
  importing_post: boolean;
  creator_defaults?: lomavisCreatorDefaultData;
  browser_extension_data?: BrowserExtensionData;
  additional_data?: {
    instagram_direct_connect?: boolean;
    instagram_music_search_url?: string;
  };
}

export interface ILomavisCreatorConfigMetadata {
  uuid: string;
  name: string;
  slug: string;
}

export interface SchedulePlanningDataType {
  time: string;
  recurrence_as_str: string;
  is_recurring_post: boolean;
  publication_delay_in_minutes?: number;
}

export interface publicationDelayDataType {
  days: null | number;
  hours: null | number;
  minutes: null | number;
  totalMinutes: null | number;
  noPublishDays: string[];
  noPublishStartTime: string;
  noPublishEndTime: string;
}

export interface CampaignType {
  /** Unique identifier (UUID v4) */
  uuid: string;

  /** Human-readable name of the campaign type */
  name: string;

  /** Hexadecimal color code (e.g. #FF6B6B) */
  color?: string;

  /** ISO 8601 timestamp for when the campaign type was created */
  created?: string;

  /** ISO 8601 timestamp for when the campaign type was last modified */
  modified?: string;

  logo_url?: string;
}

export function readLomavisCreatorConfig(): LomavisCreatorConfig | null {
  const scriptTag = document.getElementById('lomavis_creator_config');
  if (scriptTag) {
    const jsonString = scriptTag.textContent;
    if (jsonString) {
      try {
        const config: LomavisCreatorConfig = JSON.parse(jsonString);
        return config;
      } catch (e) {
        console.error('Error parsing JSON', e);
        return null;
      }
    }
  }
  return null;
}
